/**
 * Curated lung-disease groups for the "Lung Diseases" filter view.
 *
 * The MeSH tree and the classification view both expose the raw hierarchy; this
 * module is the hand-curated shortcut in front of them — a short list of the
 * conditions the Atlas is actually about, each defined in terms of the same
 * MeSH paths the other two views use (public/data/mesh-index.json).
 *
 * ── How a group is defined ────────────────────────────────────────────────
 *
 * Every protein in the MeSH index carries a list of *paths*: full ancestor
 * chains from a top-level MeSH category down to the term the protein was
 * annotated with, e.g.
 *
 *   "Respiratory Tract Diseases -> Lung Diseases -> Lung Diseases, Interstitial
 *    -> Pulmonary Fibrosis -> Idiopathic Pulmonary Fibrosis"
 *
 * A group names MeSH terms; a path matches a term when the term appears as one
 * of its segments. Because paths are full chains, naming a term also matches
 * everything *below* it — naming "Asthma" picks up "Asthma, Occupational" too.
 * That is deliberate: groups are meant to be inclusive.
 *
 * Each group has:
 *
 *   id          stable key (used in React state; never shown)
 *   label       button text
 *   description tooltip / documentation line, shown on hover
 *   match       array of clauses. A clause is an array of MeSH terms.
 *               Within a clause the terms are OR:ed; between clauses they are
 *               AND:ed. One clause is the normal case; two clauses express
 *               "annotated with both of these" (see lung-squamous below).
 *   exclude     optional MeSH terms that disqualify a path. A path containing
 *               any excluded term is ignored entirely when evaluating `match`.
 *               Used to carve a narrower term out of a broader subtree
 *               (ARDS excludes the neonatal branch).
 *   enabled     optional. Set to false to keep a group's definition on file but
 *               out of the UI, whatever it matches. Flip it back to true (or
 *               delete the line) to bring the button back.
 *   medgen      the NCBI MedGen concept the group stands for, as
 *               { uid, cui, name }. Selecting the group shows a link built from
 *               this — see medgenUrl() below. `name` is MedGen's own name for
 *               the concept, which is why it does not always match `label`.
 *
 * ── How to change the list ────────────────────────────────────────────────
 *
 * Edit DISEASE_GROUPS below: add, remove, reorder, retitle. Order here is the
 * order of the buttons. Nothing else needs touching — the filter component
 * derives its buttons, counts and tooltips from this array.
 *
 * A group whose match set is empty for the currently loaded proteins is hidden
 * rather than shown disabled, per the requirement that a group only appears
 * when at least one protein matches. `enabled: false` hides a group regardless
 * of what it matches.
 *
 * The full rationale for each definition, and the term counts they resolve to
 * today, are in diseaseGroups.md next to this file.
 */

/**
 * UI switches for the disease-group panel. Both controls below are built and
 * working; they are simply not shown at the moment. Set either to true to bring
 * it back.
 */
// The AND/OR slide switch. While hidden, selections always combine with
// DEFAULT_COMBINE_MODE.
export const SHOW_COMBINE_MODE_TOGGLE = false;
// The "Clear disease selection" button. While hidden, a selection is cleared by
// toggling its buttons off again.
export const SHOW_CLEAR_BUTTON = false;
// 'or' = a protein linked to any selected group; 'and' = to all of them.
export const DEFAULT_COMBINE_MODE = 'or';

const DISEASE_GROUPS = [
  {
    id: 'asthma',
    medgen: { uid: '2109', cui: 'C0004096', name: 'Asthma' },
    label: 'Asthma',
    description: 'MeSH "Asthma" and its subtypes (occupational, aspirin-induced).',
    match: [['Asthma']],
  },
  {
    id: 'copd',
    medgen: { uid: '9818', cui: 'C0024117', name: 'Chronic obstructive pulmonary disease (COPD)' },
    label: 'COPD',
    description:
      'MeSH "Pulmonary Disease, Chronic Obstructive", including its narrower '
      + 'terms chronic bronchitis and pulmonary emphysema.',
    match: [['Pulmonary Disease, Chronic Obstructive']],
  },
  {
    id: 'ards',
    medgen: { uid: '20540', cui: 'C0035222', name: 'Adult acute respiratory distress syndrome' },
    label: 'ARDS',
    description:
      'MeSH "Respiratory Distress Syndrome" (the adult/acute term), with the '
      + 'neonatal branch excluded.',
    match: [['Respiratory Distress Syndrome']],
    exclude: ['Respiratory Distress Syndrome, Newborn'],
  },
  {
    id: 'ipf',
    medgen: { uid: '321462', cui: 'C1800706', name: 'Idiopathic Pulmonary Fibrosis (IPF)' },
    label: 'IPF',
    description: 'MeSH "Idiopathic Pulmonary Fibrosis" (includes Hamman-Rich syndrome).',
    match: [['Idiopathic Pulmonary Fibrosis']],
  },
  {
    id: 'lung-cancer-adeno',
    medgen: { uid: '57744', cui: 'C0152013', name: 'Lung adenocarcinoma' },
    label: 'Lung Cancer Adeno',
    description: 'MeSH "Adenocarcinoma of Lung".',
    match: [['Adenocarcinoma of Lung']],
  },
  {
    id: 'lung-cancer-squamous',
    medgen: { uid: '2874', cui: 'C0007137', name: 'Squamous cell carcinoma' },
    label: 'Lung Cancer squamous',
    description:
      'Proteins annotated with both squamous cell carcinoma and a lung '
      + 'neoplasm — MeSH has no lung-specific squamous carcinoma term.',
    // Off for now: no protein carries both annotations, so the button would
    // never appear anyway. Kept so the definition is not lost.
    enabled: false,
    // Two clauses: squamous histology AND a lung site. The excluded terms are
    // the two organ-specific squamous descendants, so an oesophageal or
    // head-and-neck annotation cannot satisfy the histology clause.
    match: [['Carcinoma, Squamous Cell'], ['Lung Neoplasms']],
    exclude: ['Esophageal Squamous Cell Carcinoma', 'Squamous Cell Carcinoma of Head and Neck'],
  },
  {
    id: 'ild',
    medgen: { uid: '60064', cui: 'C0206062', name: 'Interstitial lung disease' },
    label: 'ILD',
    description:
      'Interstitial lung disease — MeSH "Lung Diseases, Interstitial" plus '
      + '"Pulmonary Fibrosis" (which is also reachable outside the lung subtree).',
    match: [['Lung Diseases, Interstitial', 'Pulmonary Fibrosis']],
  },
  {
    id: 'sarcoidosis',
    medgen: { uid: '48554', cui: 'C0036202', name: 'Sarcoidosis' },
    label: 'Sarcoidosis',
    description: 'MeSH "Sarcoidosis", including pulmonary sarcoidosis.',
    // Off for now: the only annotations in this data sit on the lymphatic and
    // immune branches, with nothing pulmonary, so the group does not earn a
    // place among the lung diseases yet.
    enabled: false,
    match: [['Sarcoidosis']],
  },
  {
    id: 'lung-cancer-nsclc',
    medgen: { uid: '40104', cui: 'C0007131', name: 'Non-small cell lung carcinoma (NSCLC)' },
    label: 'Lung Cancer NSCLC',
    description: 'MeSH "Carcinoma, Non-Small-Cell Lung".',
    match: [['Carcinoma, Non-Small-Cell Lung']],
  },
  {
    id: 'lung-cancer-sclc',
    medgen: { uid: '57450', cui: 'C0149925', name: 'Small cell lung carcinoma (SCCL)' },
    label: 'Lung Cancer SCLC',
    description: 'MeSH "Small Cell Lung Carcinoma".',
    match: [['Small Cell Lung Carcinoma']],
  },
];

/**
 * Link to a group's concept page on NCBI MedGen.
 * Returns null for a group with no `medgen` entry, so the caller can skip it.
 */
export function medgenUrl(group) {
  const uid = group && group.medgen && group.medgen.uid;
  return uid ? `https://www.ncbi.nlm.nih.gov/medgen/${uid}/` : null;
}

/**
 * Split one MeSH path string into its segments.
 * "A -> B -> C" → ["A", "B", "C"]
 */
export function splitMeshPath(path) {
  return String(path).split('->').map(s => s.trim()).filter(Boolean);
}

/**
 * Pre-split a MeSH index ({uniprotId: [pathString, ...]}) into
 * {uniprotId: [Set<segment>, ...]}, optionally restricted to knownIds.
 *
 * Done once per index so that evaluating ten groups is ten cheap Set lookups
 * per protein rather than ten re-parses of every path.
 */
export function buildPathSegmentIndex(meshIndex, knownIds) {
  const out = new Map();
  if (!meshIndex) return out;
  for (const [uid, paths] of Object.entries(meshIndex)) {
    if (knownIds && !knownIds.has(uid)) continue;
    out.set(uid, paths.map(p => new Set(splitMeshPath(p))));
  }
  return out;
}

/**
 * Set of uniprot ids matching one group, given a segment index from
 * buildPathSegmentIndex().
 */
export function matchGroup(group, segmentIndex) {
  const exclude = group.exclude || [];
  const result = new Set();
  for (const [uid, paths] of segmentIndex) {
    const eligible = exclude.length
      ? paths.filter(segs => !exclude.some(t => segs.has(t)))
      : paths;
    if (!eligible.length) continue;
    const ok = group.match.every(clause =>
      clause.some(term => eligible.some(segs => segs.has(term)))
    );
    if (ok) result.add(uid);
  }
  return result;
}

/**
 * Match every enabled group at once (`enabled: false` groups are dropped here).
 * Returns [{ group, ids: Set<uniprotId> }, ...] in configuration order,
 * groups with no match included — the caller decides to hide them.
 */
export function matchAllGroups(meshIndex, knownIds, groups = DISEASE_GROUPS) {
  const segmentIndex = buildPathSegmentIndex(meshIndex, knownIds);
  return groups
    .filter(group => group.enabled !== false)
    .map(group => ({ group, ids: matchGroup(group, segmentIndex) }));
}

/**
 * Combine the selected groups' id sets.
 * mode 'or'  → union (a protein linked to any selected group)
 * mode 'and' → intersection (linked to all of them)
 * Returns null when nothing is selected, i.e. "no filter".
 */
export function combineGroupSets(sets, mode) {
  if (!sets.length) return null;
  if (sets.length === 1) return new Set(sets[0]);
  if (mode === 'and') {
    const [first, ...rest] = sets.slice().sort((a, b) => a.size - b.size);
    const out = new Set();
    for (const id of first) {
      if (rest.every(s => s.has(id))) out.add(id);
    }
    return out;
  }
  const out = new Set();
  for (const s of sets) for (const id of s) out.add(id);
  return out;
}

export default DISEASE_GROUPS;
